// src/App.tsx

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { store, RootState } from 'store';
import { useAuthInit } from 'hooks/useAuthInit';
import { useTokenRefresh } from 'hooks/useTokenRefresh';
import { useHierarchyInit } from 'hooks/useHierarchyInit';
import { useIdleTimeout } from 'hooks/useIdleTimeout';
import { useThemeSync } from 'hooks/useThemeSync';
import LoginPage from 'pages/LoginPage';
import RegisterPage from 'pages/RegisterPage';
import VerifyEmailPage from 'pages/VerifyEmailPage';
import AppPage from 'pages/AppPage';
import AdminDashboard from 'pages/AdminDashboard';
import PrivateRoute from 'components/auth/PrivateRoute';
import AdminRoute from 'components/auth/AdminRoute';
import GuestRoute from 'components/auth/GuestRoute';
import ChangePasswordModal from 'components/auth/ChangePasswordModal';
import Toast from 'components/ui/Toast';
import ViewLoading from 'components/layout/ViewLoading';

// Each main-area view is its own chunk — only the view the URL asks for is downloaded.
const Dashboard         = lazy(() => import('components/dashboard/Dashboard'));
const MeetingsView      = lazy(() => import('components/meetings/MeetingsView'));
const FileMovementView  = lazy(() => import('components/filemovement/FileMovementView'));
const VisitorRegisterView = lazy(() => import('components/visitors/VisitorRegisterView'));
const PaToolsView       = lazy(() => import('components/patools/PaToolsView'));
const ListView          = lazy(() => import('components/tasks/TaskList'));
const KanbanView        = lazy(() => import('components/kanban/KanbanView'));
const CalendarView      = lazy(() => import('components/calendar/CalendarView'));
const MatrixView        = lazy(() => import('components/matrix/MatrixView'));
const HabitView         = lazy(() => import('components/habits/HabitView'));
const ReportView        = lazy(() => import('components/reports/ReportView'));
const RoleDashboard     = lazy(() => import('components/admin/RoleDashboard'));
const TaskDetailPage    = lazy(() => import('pages/TaskDetailPage'));
const AdminUserList     = lazy(() => import('components/admin/AdminUserList'));
const AdminUserCreate   = lazy(() => import('components/admin/AdminUserCreate'));
const RoleManager       = lazy(() => import('components/admin/RoleManager'));
const AuditLogViewer    = lazy(() => import('components/admin/AuditLogViewer'));
const AnalyticsView     = lazy(() => import('components/admin/AnalyticsView'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

// Inner component — must be inside <Provider> to use Redux hooks
function AppRoutes() {
  useAuthInit();         // once on mount: silent refresh → restore session from cookie
  useTokenRefresh();     // ongoing: proactively refresh access token 1 min before expiry
  useHierarchyInit();    // once authenticated: fetch + cache the officer hierarchy role list
  useIdleTimeout();      // ongoing: auto-logout after 30 min of no user activity
  useThemeSync();        // ongoing: mirror theme/density (uiSlice) onto <html data-theme/data-density>

  const { isAuthenticated, user } = useSelector((s: RootState) => s.auth);

  return (
    <>
      <Routes>
        <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="/admin/users" replace />} />
          <Route path="users" element={<Suspense fallback={<ViewLoading />}><AdminUserList /></Suspense>} />
          <Route path="create" element={<Suspense fallback={<ViewLoading />}><AdminUserCreate /></Suspense>} />
          <Route path="roles" element={<Suspense fallback={<ViewLoading />}><RoleManager /></Suspense>} />
          <Route path="audit" element={<Suspense fallback={<ViewLoading />}><AuditLogViewer /></Suspense>} />
          <Route path="analytics" element={<Suspense fallback={<ViewLoading />}><AnalyticsView /></Suspense>} />
          <Route path="*" element={<Navigate to="/admin/users" replace />} />
        </Route>
        <Route
          path="/"
          element={
            <PrivateRoute>
              <AppPage />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Suspense fallback={<ViewLoading />}><Dashboard /></Suspense>} />
          <Route path="meetings" element={<Suspense fallback={<ViewLoading />}><MeetingsView /></Suspense>} />
          <Route path="filemovement" element={<Suspense fallback={<ViewLoading />}><FileMovementView /></Suspense>} />
          <Route path="visitors" element={<Suspense fallback={<ViewLoading />}><VisitorRegisterView /></Suspense>} />
          <Route path="patools" element={<Suspense fallback={<ViewLoading />}><PaToolsView /></Suspense>} />
          <Route path="tasks/:listId" element={<Suspense fallback={<ViewLoading />}><ListView /></Suspense>} />
          <Route path="kanban" element={<Suspense fallback={<ViewLoading />}><KanbanView /></Suspense>} />
          <Route path="calendar" element={<Suspense fallback={<ViewLoading />}><CalendarView /></Suspense>} />
          <Route path="matrix" element={<Suspense fallback={<ViewLoading />}><MatrixView /></Suspense>} />
          <Route path="habit" element={<Suspense fallback={<ViewLoading />}><HabitView /></Suspense>} />
          <Route path="report" element={<Suspense fallback={<ViewLoading />}><ReportView /></Suspense>} />
          <Route path="hierarchy" element={<Suspense fallback={<ViewLoading />}><RoleDashboard /></Suspense>} />
          <Route path="task/:taskId" element={<Suspense fallback={<ViewLoading />}><TaskDetailPage /></Suspense>} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toast />

      {/* Forced first-login password reset (FEATURES.md §10) — blocks
          the whole app, admin-generated accounts included, until resolved. */}
      {isAuthenticated && user?.mustResetPassword && (
        <ChangePasswordModal mandatory onClose={() => {}} />
      )}
    </>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
}
